import type { InventoryCategoryRecord } from '@/renderer/types/inventory';
import {
  useInventoryCategories,
  useSaveInventoryCategory,
} from '@hooks/inventory';
import { LoadingState } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import React, { useMemo, useState } from 'react';
import { InventoryCategoryEditorModal } from './InventoryCategoryEditorModal';
import { InventorySettingsTable } from './InventorySettingsTable';
import {
  buildSaveInventoryCategoryPayload,
  InventoryCategoryFormState,
} from './inventorySettingsForm';

const EMPTY_CATEGORIES: InventoryCategoryRecord[] = [];

export function InventoryCategoriesPageContent() {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const categoriesQuery = useInventoryCategories(companyId);
  const saveCategoryMutation = useSaveInventoryCategory();

  const [search, setSearch] = useState('');
  const [editingCategory, setEditingCategory] =
    useState<InventoryCategoryRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = categoriesQuery.data ?? EMPTY_CATEGORIES;

  const filteredCategories = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    if (!searchValue) {
      return categories;
    }

    return categories.filter((category) =>
      [category.name, category.description ?? '']
        .join(' ')
        .toLowerCase()
        .includes(searchValue),
    );
  }, [categories, search]);

  if (categoriesQuery.isLoading) {
    return <LoadingState message="Loading inventory categories..." />;
  }

  const handleSave = async (form: InventoryCategoryFormState) => {
    if (!companyId) {
      return;
    }

    await saveCategoryMutation.mutateAsync(
      buildSaveInventoryCategoryPayload(companyId, form, editingCategory?.id),
    );
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-pos-primary">
              Inventory items
            </p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-950">
              Categories
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Keep item grouping consistent so stock items, recipes, and reports
              use the same business language.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-pos-primary/10 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
                Total categories
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-slate-950">
                {categories.length}
              </p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Active categories
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-slate-950">
                {categories.filter((item) => item.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <InventorySettingsTable
        title="Inventory categories"
        description="Categories help group stock items for purchasing, recipe setup, reorder review, and reporting."
        searchLabel="Find category"
        searchValue={search}
        createLabel="New category"
        rows={filteredCategories.map((category) => ({
          id: category.id,
          title: category.name,
          subtitle: category.description,
          meta: `Updated ${new Date(category.updated_at).toLocaleDateString()}`,
          status: category.is_active,
        }))}
        emptyMessage="No categories match this filter yet."
        onSearchChange={setSearch}
        onCreate={() => {
          setEditingCategory(null);
          setIsModalOpen(true);
        }}
        onEdit={(id) => {
          const category = categories.find((item) => item.id === id) ?? null;
          setEditingCategory(category);
          setIsModalOpen(true);
        }}
        onToggleStatus={(id) => {
          const category = categories.find((item) => item.id === id);
          if (!category || !companyId) {
            return;
          }

          saveCategoryMutation.mutateAsync({
            id: category.id,
            companyId,
            name: category.name,
            description: category.description,
            isActive: !category.is_active,
          });
        }}
      />

      {isModalOpen ? (
        <InventoryCategoryEditorModal
          category={editingCategory}
          isSaving={saveCategoryMutation.isPending}
          onClose={() => {
            setIsModalOpen(false);
            setEditingCategory(null);
          }}
          onSave={handleSave}
        />
      ) : null}
    </div>
  );
}
